import type { TFunction } from "i18next";
import { builtInToolName } from "../../../api/content/processDefinitions";
import type { StepDisplayState } from "../../../api/threads/liveMessages";
import { stepStateText } from "../../threads/status/stepStateWords";
import type { Selector } from "../../../api/content/authoring/selectorDecoders";
import type { HumanStepDefinition } from "../../../api/content/authoring/stepDecoders";
import { isPlacementNode, type GraphNode, type GraphPort, type SelectionGraphNode, type SourceGraphNode, type StepGraphNode, type TermsGraphNode } from "../model/workflowGraph";
import { inputCardMeta, sourceCardFacts } from "../inputCards/inputCardFacts";
import { itemTypeText, kindsText, spaced, stepKindText } from "../inputCards/typeWords";
import i18next from "i18next";
import { inputLabel } from "../../processAuthoring";

/**
 * The words for one graph node, shared by the graph, the list view and the editor so all
 * say the same: its number, title, kind, one key fact, what it does, whether it is
 * optional, its ports' words, and the full sentence a screen reader announces (UX-22:
 * every symbol has a text alternative).
 */
export interface NodeText {
  readonly title: string;
  readonly kind: string;
  /** The one key fact a collapsed card shows, such as what a step writes. */
  readonly fact: string | null;
  /** What it does, from its definition: the detailed density shows it. */
  readonly does: string | null;
  /** The step's number in its Workflow, such as `1.1` (see `workflowGraph`). */
  readonly number: string | null;
  /** Set for a step that starts only when someone starts it: it is optional. */
  readonly optional: string | null;
  readonly label: string;
}

export function nodeText(node: GraphNode, state: StepDisplayState | null, t: TFunction): NodeText {
  const { title, kind, fact, does } = parts(node, t);
  const number = isPlacementNode(node) ? node.number : null;
  const optional = node.kind === "step" && node.start === "on_request" ? t("workflows.node.onRequest") : null;
  const inputs = node.ports.filter((port) => port.direction === "input").map((port) => portSpoken(node, port, t));
  const outputs = node.ports.filter((port) => port.direction === "output" && port.name !== "out").map((port) => portSpoken(node, port, t));
  const sentence = [
    number === null ? null : t("workflows.node.number", { number }),
    `${kind}: ${title}`,
    fact,
    optional,
    inputs.length === 0 ? null : t("workflows.node.takes", { ports: inputs.join(", ") }),
    outputs.length === 0 ? null : t("workflows.node.gives", { ports: outputs.join(", ") }),
    node.band === "input" ? t("workflows.node.isInput") : null,
    (node.kind === "sub_workflow" || node.kind === "artifact") && node.expandable ? t(node.expanded ? "workflows.node.isExpanded" : "workflows.node.isCollapsed") : null,
    state === null ? null : t("workflows.node.state", { state: stepStateText(state, t) }),
  ];
  return { title, kind, fact, does, number, optional, label: sentence.filter((part) => part !== null).join(". ") };
}

/**
 * A port's name wherever a port shows (cards, lists, context, change lists): an input by its declared label in the
 * reader's language (vocabulary row 16), a step's result by what it is, else the slot as the step names it.
 * `language` defaults to the app's current one, which `t` translates into: i18next's own instance, read without
 * the app's i18n setup module, so this stays a text module without side effects.
 */
export function portLabel(node: GraphNode, port: GraphPort, t: TFunction, language: string | undefined = i18next.resolvedLanguage): string {
  if (port.direction === "input" && node.kind === "step" && node.step !== null) {
    const declared = inputLabel(node.step.definition, port.name, language);
    if (declared !== null) return declared;
  }
  if (port.direction === "output" && node.kind === "step" && node.step !== null) {
    if (port.name === "result") {
      const definition = node.step.definition;
      const word = definition.kind === "human" && definition.response.kind === "review_output" ? "reviewed" : (port.kinds[0] ?? "result");
      return t(`workflows.node.results.${word}`, { defaultValue: spaced(port.name) });
    }
    if (port.name === "choice") {
      return t("workflows.node.results.choice");
    }
  }
  return spaced(port.name);
}

/**
 * A port's data type as visible text (F40, R-10): one kind, a union as "Text or
 * Annotations", and "Type not declared" where the definition names none. Never guessed. An
 * input a source fills says what it reads, as the source's card does (F61).
 */
export function portType(port: GraphPort, t: TFunction): string {
  return port.reads === null ? kindsText(port.kinds, t) : itemTypeText(port.reads, t);
}

/**
 * What a port's card shows: its purpose and its type, "Candidate claims · Annotations".
 * A step's result that is named by its type says it once, as its type.
 */
export function portWords(node: GraphNode, port: GraphPort, t: TFunction): { readonly purpose: string; readonly type: string | null } {
  const purpose = portLabel(node, port, t);
  const type = portType(port, t);
  // "annotations · Annotations" says it once, as the type.
  return purpose.toLocaleLowerCase() === type.toLocaleLowerCase() ? { purpose: type, type: null } : { purpose, type };
}

/** The words of a port's need: Required, Optional, or not declared; `null` for an output. */
export function portNeed(port: GraphPort, t: TFunction): string | null {
  return port.need === null ? null : t(`workflows.node.port.needs.${port.need}`);
}

/** A port in the card's spoken sentence: "Candidate claims · Annotations, required". */
function portSpoken(node: GraphNode, port: GraphPort, t: TFunction): string {
  const { purpose, type } = portWords(node, port, t);
  const need = portNeed(port, t);
  return [type === null ? purpose : `${purpose} · ${type}`, need].filter((part) => part !== null).join(", ");
}

/**
 * What a port's words do not show: whether the step needs an input and whether nothing
 * fills it, as module B's check says (F102 CP-04: a binding, the fork point, a Workflow input
 * or a default fills it; unsaid where no check is at hand), or whether a later step uses an
 * output. In a Thread, a port's connection state ("Missing", "Waiting for step 2") is a
 * further fact the Thread view adds.
 */
export function portFacts(port: GraphPort, t: TFunction): string {
  if (port.direction === "input") {
    return [portNeed(port, t), port.filledBy === "missing" ? t("workflows.node.port.unfilled") : null].filter((part) => part !== null).join("; ");
  }
  return t(port.connected ? "workflows.node.port.used" : "workflows.node.port.unused");
}

/** A port's tooltip: its purpose and type, then its facts. */
export function portTooltip(node: GraphNode, port: GraphPort, t: TFunction): string {
  const { purpose, type } = portWords(node, port, t);
  return `${type === null ? purpose : `${purpose} · ${type}`}; ${portFacts(port, t)}`;
}

/** How many characters a port's words take on its card, for the card's width (`cardWords`). */
export function portWordsLength(node: GraphNode, port: GraphPort, t: TFunction): number {
  const { purpose, type } = portWords(node, port, t);
  return type === null ? purpose.length : purpose.length + 3 + type.length;
}

function parts(node: GraphNode, t: TFunction): Pick<NodeText, "title" | "kind" | "fact" | "does"> {
  switch (node.kind) {
    case "step":
      if (node.step === null) {
        return { title: node.stepKey, kind: t("workflows.node.kinds.unreadable"), fact: node.reference, does: null };
      }
      return { title: node.step.name, kind: stepKindText(node.step.kind, t), fact: stepFact(node, t), does: node.step.description === "" ? null : node.step.description };
    case "sub_workflow":
      if (node.workflow === null) {
        return { title: node.stepKey, kind: t("workflows.node.kinds.unreadable"), fact: node.reference, does: null };
      }
      return {
        title: node.workflow.name,
        kind: t("workflows.node.kinds.sub_workflow"),
        fact: t("workflows.node.facts.subWorkflow", { count: node.stepNames.length, steps: node.stepNames.join(", ") }),
        does: node.workflow.description === "" ? null : node.workflow.description,
      };
    case "abstract_step":
      // No definition yet: the Workflow that nests this one binds it (ADR-0043 D1).
      return { title: node.label, kind: t("workflows.node.kinds.abstract_step"), fact: node.required ? null : t("workflows.node.facts.unboundOptional"), does: node.description === "" ? null : node.description };
    case "artifact":
      return sourceParts(node, node.artifact.name, null, t);
    case "selection":
      return sourceParts(node, selectionTitle(node, t), selectionFact(node, t), t);
    case "terms":
      return sourceParts(node, termsTitle(node, t), termsFact(node, t), t);
    case "item":
      return sourceParts(node, node.item.name, null, t);
  }
}

/** An input card (F61): its type as its kind, then its version and origin before what it holds. */
function sourceParts(node: SourceGraphNode, title: string, holds: string | null, t: TFunction): Pick<NodeText, "title" | "kind" | "fact" | "does"> {
  const facts = sourceCardFacts(node, title, t);
  const fact = [inputCardMeta(facts, t), holds].filter((part) => part !== null).join(" · ");
  return { title, kind: facts.type, fact: fact === "" ? null : fact, does: null };
}

/** What a step writes and where it goes, how a person answers, or which tool runs. */
function stepFact(node: StepGraphNode, t: TFunction): string | null {
  const definition = node.step?.definition;
  switch (definition?.kind) {
    case "agent": {
      const output = definition.output;
      if (output.kind === "annotations") {
        return t(output.destination === "knowledge_data" ? "workflows.node.facts.annotationsToData" : "workflows.node.facts.annotations");
      }
      if (output.kind === "change_set") {
        return t(output.required ? "workflows.node.facts.changeSet" : "workflows.node.facts.changeSetOptional");
      }
      return t(output.kind === "instance_data" ? "workflows.node.facts.instanceData" : "workflows.node.facts.text");
    }
    case "human":
      return humanResponseFact(definition.response, t, node.step?.choiceLabels);
    case "tool":
      return toolFact(definition.tool, node, t);
    case "talk": // offered by a review step, never placed in a Workflow (F105)
    case undefined:
      return null;
  }
}

/** How a person answers: the card's key fact and the open card's "Answer". */
export function humanResponseFact(response: HumanStepDefinition["response"], t: TFunction, choiceLabels: Readonly<Record<string, string>> | null | undefined): string {
  switch (response.kind) {
    case "review_output":
      return t(response.comment === "required" ? "workflows.node.facts.reviewWithComment" : "workflows.node.facts.review");
    case "choice":
      return t("workflows.node.facts.choice", { options: response.options.map((option) => choiceLabels?.[option.key] ?? option.label).join(", ") });
    case "workspace_file":
      return t("workflows.node.facts.workspaceFile", { suffixes: response.suffixes.join(", ") });
    case "text":
      return t("workflows.node.facts.answer");
    case "propose_change":
      return t("workflows.node.facts.proposeChange");
    case "review_change_set":
      return t("workflows.node.facts.reviewChangeSet");
    case "decide_results":
      return t("workflows.node.facts.decideResults");
    case "release_part":
      return t("workflows.node.facts.releasePart", { part: t(`workflows.node.releaseParts.${response.part}`) });
    case "keep_memory":
      return t("workflows.node.facts.keepMemory");
    case "review_memory":
      return t("workflows.node.facts.reviewMemory");
  }
}

/** "Built-in run_checks, no model" or "render_annotation_guideline, no model". */
export function toolFact(reference: string, node: StepGraphNode, t: TFunction): string {
  const model = t(`workflows.context.card.model.${node.tool?.modelUse ?? "unknown"}`);
  const builtIn = builtInToolName(reference);
  return builtIn === null ? t("workflows.node.facts.tool", { tool: node.tool?.name ?? t("workflows.node.projectTool"), model }) : t("workflows.node.facts.builtInTool", { tool: builtIn, model });
}

function selectionTitle(node: SelectionGraphNode, t: TFunction): string {
  if (node.item !== null) {
    return node.item.name;
  }
  return node.selector.kind === "competency_questions" && node.selector.tags.length === 0 ? t("workflows.node.allQuestions") : t(`workflows.node.selectors.${node.selector.kind}`);
}

/**
 * What the source holds, and the settings every step reading it shares: a label list's
 * labels, read with guidelines and which examples; the questions, with or without SPARQL.
 */
function selectionFact(node: SelectionGraphNode, t: TFunction): string | null {
  const { selector } = node;
  switch (selector.kind) {
    case "label_list": {
      const what = [node.count === null ? null : t("workflows.node.facts.labels", { count: node.count }), labelListReadNote(onlyOfKind(node.reads, "label_list"), t)].filter((part) => part !== null && part !== "");
      return what.length === 0 ? null : what.join(", ");
    }
    case "competency_questions": {
      const sparql = sparqlNote(onlyOfKind(node.reads, "competency_questions"), t);
      const questions = selector.tags.length > 0 ? t("workflows.node.facts.taggedQuestions", { tags: selector.tags.join(", ") }) : node.count === null ? null : t("workflows.node.facts.questions", { count: node.count });
      const what = [questions, sparql].filter((part) => part !== null);
      return what.length === 0 ? null : what.join(", ");
    }
    case "few_shot_examples": {
      const maximum = shared(onlyOfKind(node.reads, "few_shot_examples").map((read) => read.maximum_examples));
      return maximum === null ? null : t("workflows.node.facts.fewShot", { count: maximum });
    }
  }
}

/** "with guidelines, examples that match the entry": what every read of a label list includes. */
export function labelListReadNote(reads: readonly Extract<Selector, { kind: "label_list" }>[], t: TFunction): string {
  const guidelines = shared(reads.map((read) => read.include_guidelines));
  const examples = shared(reads.map((read) => read.include_examples));
  return [guidelines === true ? t("workflows.node.facts.withGuidelines") : null, examples === null || examples === "none" ? null : t(`workflows.node.facts.examples.${examples}`)].filter((part) => part !== null).join(", ");
}

/** "with SPARQL" or "without SPARQL", where every read of the questions agrees; else `null`. */
export function sparqlNote(reads: readonly Extract<Selector, { kind: "competency_questions" }>[], t: TFunction): string | null {
  const sparql = shared(reads.map((read) => read.include_sparql));
  return sparql === null ? null : t(sparql ? "workflows.node.facts.withSparql" : "workflows.node.facts.withoutSparql");
}

/** The one value all share; `null` when they differ or there are none. */
function shared<T>(values: readonly T[]): T | null {
  const [first] = values;
  return first !== undefined && values.every((value) => value === first) ? first : null;
}

function onlyOfKind<K extends Selector["kind"]>(reads: readonly Selector[], kind: K): Extract<Selector, { kind: K }>[] {
  return reads.filter((read): read is Extract<Selector, { kind: K }> => read.kind === kind);
}

/** "Classes of the data model", or the modules it names. */
function termsTitle(node: TermsGraphNode, t: TFunction): string {
  const kinds = node.selector.term_kinds.map((kind) => t(`workflows.node.termKinds.${kind}`));
  // Two kinds are named; more read as "terms", and the settings list them.
  const what = kinds.length === 1 ? (kinds[0] ?? "") : kinds.length === 2 ? t("workflows.node.termKindsMany", { first: kinds[0] ?? "", last: kinds[1] ?? "" }) : t("workflows.node.termKindsAll");
  return node.modules.length === 0 ? t("workflows.node.termsOfModel", { kinds: what }) : t("workflows.node.termsOfModules", { kinds: what, modules: node.modules.join(", ") });
}

/** The properties every read shows with each term: their names, or the item that names them. */
function termsFact(node: TermsGraphNode, t: TFunction): string | null {
  const properties = shared(node.reads.map((read) => read.annotation_properties.join("\n")));
  if (properties !== null && properties !== "") {
    return t("workflows.node.facts.termProperties", { properties: properties.split("\n").map(propertyName).join(", ") });
  }
  if (node.propertiesFrom !== null) {
    return t("workflows.node.facts.termPropertiesFrom", { item: node.propertiesFrom });
  }
  const render = shared(node.reads.map((read) => read.render));
  return render === null ? null : t(`workflows.node.facts.termRender.${render}`);
}

/** `http://example.invalid/petitions#triggerPhrase` → "trigger phrase". */
export function propertyName(iri: string): string {
  const local = iri.split(/[#/]/).at(-1) ?? iri;
  return local.replace(/([a-z])([A-Z])/g, "$1 $2").replaceAll("_", " ").toLowerCase();
}
